import { useState, useEffect } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, CreditCard, Bank, Money, Coins, PiggyBank, Wallet, CurrencyDollar, Receipt, Barcode, PixLogo } from "@phosphor-icons/react";

// Available icons for payment methods
const ICON_OPTIONS = [
  { value: "CreditCard", label: "Cartão de Crédito", Icon: CreditCard },
  { value: "Bank", label: "Banco", Icon: Bank },
  { value: "Money", label: "Dinheiro", Icon: Money },
  { value: "Coins", label: "Moedas", Icon: Coins },
  { value: "PiggyBank", label: "Poupança", Icon: PiggyBank },
  { value: "Wallet", label: "Carteira", Icon: Wallet },
  { value: "CurrencyDollar", label: "Dólar", Icon: CurrencyDollar },
  { value: "Receipt", label: "Recibo", Icon: Receipt },
  { value: "Barcode", label: "Boleto", Icon: Barcode },
  { value: "PixLogo", label: "PIX", Icon: PixLogo },
];

// Map icon name to component
const getIconComponent = (iconName) => {
  const found = ICON_OPTIONS.find(opt => opt.value === iconName);
  return found ? found.Icon : CreditCard;
};

export default function PaymentMethodsPage() {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMethod, setEditingMethod] = useState(null);
  const [formData, setFormData] = useState({ name: "", icon: "CreditCard" });

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get("/payment-methods");
      setPaymentMethods(response.data);
    } catch (error) {
      toast.error("Erro ao carregar formas de pagamento");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: "", icon: "CreditCard" });
    setEditingMethod(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMethod) {
        await api.put(`/payment-methods/${editingMethod.id}`, formData);
        toast.success("Forma de pagamento atualizada!");
      } else {
        await api.post("/payment-methods", formData);
        toast.success("Forma de pagamento criada!");
      }
      setDialogOpen(false);
      resetForm();
      fetchPaymentMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar forma de pagamento");
    }
  };

  const handleEdit = (method) => {
    setEditingMethod(method);
    setFormData({ name: method.name, icon: method.icon });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir esta forma de pagamento?")) return;
    try {
      await api.delete(`/payment-methods/${id}`);
      toast.success("Forma de pagamento excluída");
      fetchPaymentMethods();
    } catch (error) {
      toast.error("Erro ao excluir forma de pagamento");
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-20 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-methods-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Formas de Pagamento</h1>
          <p className="text-muted-foreground mt-1">
            Defina como você paga suas contas
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="tracing-beam gap-2" data-testid="new-payment-method-button">
              <Plus size={18} weight="bold" />
              Nova Forma
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingMethod ? "Editar Forma de Pagamento" : "Nova Forma de Pagamento"}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label>Nome</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Cartão Nubank, PIX..."
                  required
                  data-testid="payment-method-name-input"
                />
              </div>

              <div className="space-y-3">
                <Label>Ícone</Label>
                <Select
                  value={formData.icon}
                  onValueChange={(value) => setFormData({ ...formData, icon: value })}
                >
                  <SelectTrigger data-testid="payment-method-icon-select">
                    <SelectValue placeholder="Selecione um ícone" />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map(({ value, label, Icon }) => (
                      <SelectItem key={value} value={value}>
                        <div className="flex items-center gap-2">
                          <Icon size={18} />
                          {label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Preview */}
              <div className="p-4 rounded-lg border bg-muted/30">
                <Label className="text-sm text-muted-foreground mb-2 block">Preview</Label>
                <div className="flex items-center gap-3">
                  {(() => {
                    const IconComponent = getIconComponent(formData.icon);
                    return (
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <IconComponent size={20} className="text-primary" weight="fill" />
                      </div>
                    );
                  })()}
                  <div>
                    <p className="font-medium">{formData.name || "Nome da Forma"}</p>
                    <p className="text-sm text-muted-foreground">Forma de pagamento</p>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline">Cancelar</Button>
                </DialogClose>
                <Button type="submit" data-testid="payment-method-submit-button">
                  {editingMethod ? "Salvar" : "Criar"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Payment Methods Grid */}
      {paymentMethods.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <CreditCard size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhuma forma de pagamento cadastrada</p>
            <Button 
              variant="link" 
              onClick={() => setDialogOpen(true)}
              className="mt-2"
            >
              Criar primeira forma de pagamento
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paymentMethods.map((method, idx) => {
            const IconComponent = getIconComponent(method.icon);
            return (
              <Card 
                key={method.id} 
                className="card-hover stagger-item overflow-hidden"
                data-testid={`payment-method-card-${method.id}`}
              >
                <div className="h-2 bg-primary" />
                <CardContent className="p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <IconComponent size={20} className="text-primary" weight="fill" />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold">{method.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          {ICON_OPTIONS.find(o => o.value === method.icon)?.label || "Pagamento"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(method)}
                      className="gap-1.5 flex-1"
                      data-testid={`edit-payment-method-${method.id}`}
                    >
                      <PencilSimple size={16} />
                      Editar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(method.id)}
                      className="gap-1.5 text-destructive hover:text-destructive flex-1"
                      data-testid={`delete-payment-method-${method.id}`}
                    >
                      <Trash size={16} />
                      Excluir
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
